import { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

import {
  formatLogTime,
  hwcgLogRecipientSelectable,
  hwcgNoticeResendUserIds,
  type GroupNoticeLogRecipient,
  type GroupNoticeLogRow,
} from './hwcgActivationLogModel';

function typeLabel(action: GroupNoticeLogRow['action']): string {
  return action === 'notify' ? 'Notification' : 'Activation request';
}

const emptyRecipient = (notificationId: string): GroupNoticeLogRecipient => ({
  key: notificationId,
  userGuid: null,
  name: '—',
  delivery: '—',
  acknowledged: '—',
  note: null,
});

/**
 * Notices sent to one Group Actions group. Sent and type span the recipients,
 * and each recipient is a row so delivery and acknowledgement line up. A notice
 * can be resent to the PRATUS users checked on that row.
 */
export function HwcgGroupNoticeLog({
  rows,
  isLoading = false,
  isError = false,
  resendPending = false,
  onResend,
}: {
  rows: GroupNoticeLogRow[];
  isLoading?: boolean;
  isError?: boolean;
  resendPending?: boolean;
  onResend?: (notificationId: string, userGuids: string[]) => void;
}) {
  const [selected, setSelected] = useState<Record<string, string[]>>({});

  if (isLoading) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">Loading log…</p>
    );
  }
  if (isError) {
    return (
      <p className="tw:m-0 tw:text-xs tw:text-destructive">
        Could not load the log.
      </p>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Sent</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Recipients</TableHead>
          <TableHead>Delivery</TableHead>
          <TableHead>Acknowledged</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.length === 0 ? (
          <TableRow>
            <TableCell
              colSpan={5}
              className="tw:text-xs tw:text-muted-foreground"
            >
              No notices have been sent to this group yet.
            </TableCell>
          </TableRow>
        ) : (
          rows.map((row) => {
            const recipients =
              row.recipients.length === 0
                ? [emptyRecipient(row.notificationId)]
                : row.recipients;
            const chosen = hwcgNoticeResendUserIds(
              recipients,
              selected[row.notificationId] ?? []
            );
            return recipients.map((recipient, index) => (
              <TableRow key={`${row.notificationId}:${recipient.key}`}>
                {index === 0 ? (
                  <>
                    <TableCell
                      rowSpan={recipients.length}
                      className="tw:!h-auto tw:text-xs tw:!align-middle"
                    >
                      {formatLogTime(row.sentAt)}
                    </TableCell>
                    <TableCell
                      rowSpan={recipients.length}
                      className="tw:!h-auto tw:text-xs tw:!align-middle"
                    >
                      <div>{typeLabel(row.action)}</div>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="tw:mt-2"
                        disabled={chosen.length === 0 || resendPending}
                        onClick={() => {
                          onResend?.(row.notificationId, chosen);
                          setSelected((current) => ({
                            ...current,
                            [row.notificationId]: [],
                          }));
                        }}
                      >
                        Resend
                      </Button>
                    </TableCell>
                  </>
                ) : null}
                <TableCell className="tw:!h-auto tw:text-xs tw:!align-middle">
                  <div className="tw:flex tw:items-center tw:gap-2">
                    {hwcgLogRecipientSelectable(recipient) &&
                    recipient.userGuid ? (
                      <Checkbox
                        aria-label={`Resend to ${recipient.name}`}
                        checked={chosen.includes(recipient.userGuid)}
                        disabled={resendPending}
                        onCheckedChange={(value) => {
                          const userGuid = recipient.userGuid;
                          if (!userGuid) return;
                          setSelected((current) => {
                            const existing = current[row.notificationId] ?? [];
                            const next =
                              value === true
                                ? [...existing, userGuid]
                                : existing.filter((id) => id !== userGuid);
                            return {
                              ...current,
                              [row.notificationId]: next,
                            };
                          });
                        }}
                      />
                    ) : null}
                    <span>{recipient.name}</span>
                  </div>
                </TableCell>
                <TableCell className="tw:!h-auto tw:text-xs tw:!align-middle">
                  {recipient.delivery}
                </TableCell>
                <TableCell className="tw:!h-auto tw:text-xs tw:!align-middle">
                  <div>{recipient.acknowledged}</div>
                  {recipient.note ? (
                    <div className="tw:mt-1 tw:whitespace-pre-wrap tw:text-muted-foreground">
                      {recipient.note}
                    </div>
                  ) : null}
                </TableCell>
              </TableRow>
            ));
          })
        )}
      </TableBody>
    </Table>
  );
}
